import React, { useCallback, useEffect, useState } from "react";
import { FolderOpen } from "lucide-react";
import CrudModule from "@/components/CrudModule";
import { AiImport } from "@/components/AiImport";
import { BatchImport } from "@/components/BatchImport";
import { DocFolders } from "@/components/DocFolders";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";

const NORMS = ["ISO 45001", "ISO 14001", "ISO 9001", "NR-01", "NR-06", "NR-12", "NR-13", "Geral"];
const TYPES = ["Manual", "Política", "Procedimento", "Laudo", "Certificado", "Ficha", "FISPQ", "Documento"];

export default function Documents() {
  const { canEdit } = useAuth();
  const [folders, setFolders] = useState([]);
  const [selected, setSelected] = useState(null);
  const [extra, setExtra] = useState({ types: [], norms: [] });
  const [k, setK] = useState(0);
  const [tick, setTick] = useState(0);
  const loadFolders = useCallback(() => { api.get("/doc-folders").then(({ data }) => setFolders(data)).catch(() => {}); }, []);
  useEffect(() => {
    loadFolders();
    api.get("/documents").then(({ data }) => setExtra({
      types: [...new Set(data.map((d) => d.doc_type).filter(Boolean))],
      norms: [...new Set(data.map((d) => d.norm).filter(Boolean))],
    })).catch(() => {});
  }, [loadFolders, k, tick]);
  const onReload = (newName, oldName) => {
    loadFolders();
    if (selected && selected === oldName) setSelected(newName || null);
    if (oldName) setK((x) => x + 1);
  };
  const rowFilter = useCallback((r) => !selected || r.folder === selected, [selected]);
  const names = folders.map((f) => f.name);
  const reloadAll = () => { loadFolders(); setK((x) => x + 1); };

  return (
    <CrudModule
      key={k}
      title="GED — Repositório de Documentos"
      subtitle={selected ? `Pasta: ${selected}` : "Repositório geral de documentos — pastas, títulos e tipos livres"}
      icon={FolderOpen}
      endpoint="documents"
      exportKey="documents"
      testid="documents"
      hideBatchImport
      rowFilter={rowFilter}
      onChanged={() => setTick((x) => x + 1)}
      sidebar={<DocFolders folders={folders} selected={selected} onSelect={setSelected} onReload={onReload} />}
      headerExtra={<>
        <AiImport mode="document" onDone={reloadAll} />
        {canEdit && <BatchImport target="documents" idKey="file_id" nameKey="file_name" folders={names} defaultFolder={selected} onDone={reloadAll} testid="documents-batch" />}
      </>}
      columns={[
        { key: "title", label: "Título" },
        { key: "folder", label: "Pasta" },
        { key: "doc_type", label: "Tipo" },
        { key: "norm", label: "Norma" },
        { key: "status", label: "Status", badge: true },
        { key: "valid_until", label: "Validade", badge: "validity" },
        { key: "file", label: "Arquivo", file: true, idKey: "file_id" },
      ]}
      fields={[
        { name: "file", label: "Arquivo (upload)", type: "file", idKey: "file_id", nameKey: "file_name", full: true },
        { name: "title", label: "Título / nome do documento", type: "text", required: true, full: true },
        { name: "folder", label: "Pasta", type: "select", options: names.length ? names : ["[01] Manuais e Políticas de SSMA"], default: selected || names[0] || "[01] Manuais e Políticas de SSMA", full: true },
        { name: "doc_type", label: "Tipo (escolha ou digite um novo)", type: "combo", options: [...new Set([...TYPES, ...extra.types])], default: "Documento" },
        { name: "norm", label: "Norma (escolha ou digite uma nova)", type: "combo", options: [...new Set([...NORMS, ...extra.norms])], default: "Geral" },
        { name: "status", label: "Status", type: "select", options: ["Válido", "Em Revisão", "Vencido"], default: "Válido" },
        { name: "valid_until", label: "Válido até", type: "date" },
        { name: "year", label: "Ano", type: "text" },
        { name: "signature", label: "Assinatura eletrônica (aprovação)", type: "signature", full: true },
      ]}
    />
  );
}
